import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Check, FileText, ShieldCheck, ClipboardCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  full_name: '',
  date_of_birth: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  allergies_conditions: '',
  consent_treatment: false,
  consent_hipaa: false,
  consent_financial: false,
  consent_media: false,
  is_minor: false,
  guardian_name: '',
  guardian_relationship: '',
  signature: '',
};

const Field = ({ label, required, children, testId }) => (
  <label className="block" data-testid={testId ? `${testId}-wrapper` : undefined}>
    <span className="block text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-2">
      {label} {required && <span className="text-rose-600">*</span>}
    </span>
    {children}
  </label>
);

const inputCls = 'w-full border border-black/15 px-4 py-3 text-[14px] focus:outline-none focus:border-[#c9a961] bg-white';

const ConsentCheckbox = ({ checked, onChange, label, description, testId }) => (
  <label className="flex items-start gap-3 cursor-pointer" data-testid={`${testId}-wrapper`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testId}
      className="mt-1 w-4 h-4 accent-[#c9a961] flex-shrink-0"
    />
    <div>
      <p className="text-[14px] text-[#1c1e20] font-medium">{label}</p>
      {description && <p className="text-[13px] text-[#4a4a4a] mt-1 leading-relaxed">{description}</p>}
    </div>
  </label>
);

const PatientForms = () => {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consent_treatment || !form.consent_hipaa || !form.consent_financial) {
      setError('Please review and check each of the required consents before signing.');
      return;
    }
    if (form.signature.trim().toLowerCase() !== form.full_name.trim().toLowerCase()) {
      setError('Your typed signature must exactly match your full name.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/patient-forms/consent`, form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (ex) {
      setError(ex?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] bg-[#faf6f0] py-20 px-6">
        <div className="max-w-[720px] mx-auto bg-white border border-[#c9a961]/40 p-10 md:p-14 text-center shadow-sm">
          <div className="w-14 h-14 rounded-full bg-[#c9a961]/15 border border-[#c9a961] flex items-center justify-center mx-auto mb-5">
            <Check size={26} className="text-[#c9a961]" strokeWidth={2} />
          </div>
          <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-semibold mb-3">CONSENT ON FILE</p>
          <h1
            className="font-serif text-4xl text-[#1c1e20] mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            data-testid="consent-success-heading"
          >
            Thank you, {form.full_name.split(' ')[0] || 'client'}.
          </h1>
          <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mb-6" />
          <p className="text-[#4a4a4a] leading-relaxed">
            Your signed consent form has been securely received. A confirmation copy is on its way to
            <strong> {form.email}</strong>. You won&rsquo;t need to sign again unless your information
            materially changes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-160px)] bg-[#faf6f0] py-14 md:py-20 px-6" data-testid="patient-forms-page">
      <div className="max-w-[880px] mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-3">PATIENT FORMS</p>
          <h1
            className="font-serif text-5xl md:text-6xl leading-[1.05] text-[#1c1e20]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            Client Consent Form
          </h1>
          <div className="w-16 h-[2px] bg-[#c9a961] mx-auto my-5" />
          <p className="text-[#4a4a4a] leading-relaxed max-w-[640px] mx-auto text-[15px]">
            Please complete this consent form before your first appointment. It covers specimen
            collection, HIPAA privacy practices, and financial responsibility. Everything is stored
            securely and referenced only by Aurelia clinical staff.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white border border-black/5 p-8 md:p-12 shadow-sm space-y-10" data-testid="consent-form">
          {/* Client Information */}
          <section>
            <div className="flex items-center gap-2 mb-1">
              <FileText size={16} className="text-[#c9a961]" strokeWidth={1.6} />
              <h2
                className="font-serif text-2xl text-[#1c1e20]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                Client information
              </h2>
            </div>
            <p className="text-[13px] text-[#6b6b6b] mb-6">Required fields are marked with an asterisk.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="FULL LEGAL NAME" required testId="full-name">
                <input required value={form.full_name} onChange={(e) => set('full_name', e.target.value)} data-testid="full-name" className={inputCls} />
              </Field>
              <Field label="DATE OF BIRTH" required testId="dob">
                <input required type="date" value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} data-testid="dob" className={inputCls} />
              </Field>
              <Field label="EMAIL" required testId="email">
                <input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="email" className={inputCls} />
              </Field>
              <Field label="PHONE" required testId="phone">
                <input required value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="phone" className={inputCls} />
              </Field>
              <Field label="STREET ADDRESS">
                <input value={form.address} onChange={(e) => set('address', e.target.value)} className={inputCls} />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="CITY">
                  <input value={form.city} onChange={(e) => set('city', e.target.value)} className={inputCls} />
                </Field>
                <Field label="STATE">
                  <input value={form.state} onChange={(e) => set('state', e.target.value)} maxLength={2} className={inputCls} />
                </Field>
                <Field label="ZIP">
                  <input value={form.zip_code} onChange={(e) => set('zip_code', e.target.value)} className={inputCls} />
                </Field>
              </div>
              <Field label="EMERGENCY CONTACT NAME">
                <input value={form.emergency_contact_name} onChange={(e) => set('emergency_contact_name', e.target.value)} className={inputCls} />
              </Field>
              <Field label="EMERGENCY CONTACT PHONE">
                <input value={form.emergency_contact_phone} onChange={(e) => set('emergency_contact_phone', e.target.value)} className={inputCls} />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="ALLERGIES OR RELEVANT MEDICAL CONDITIONS">
                <textarea rows={2} value={form.allergies_conditions} onChange={(e) => set('allergies_conditions', e.target.value)} placeholder="Latex allergy, needle-phobia, prior fainting, blood-thinners, etc." className={`${inputCls} resize-none`} />
              </Field>
            </div>
          </section>

          {/* Minor */}
          <section>
            <ConsentCheckbox
              checked={form.is_minor}
              onChange={(v) => set('is_minor', v)}
              label="This form is being completed for a minor under 18."
              description="If checked, a parent or legal guardian must complete the fields below and sign on the minor's behalf."
              testId="is-minor"
            />
            {form.is_minor && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5 pl-7">
                <Field label="PARENT / GUARDIAN NAME" required testId="guardian-name">
                  <input required value={form.guardian_name} onChange={(e) => set('guardian_name', e.target.value)} data-testid="guardian-name" className={inputCls} />
                </Field>
                <Field label="RELATIONSHIP TO MINOR" required testId="guardian-relationship">
                  <input required value={form.guardian_relationship} onChange={(e) => set('guardian_relationship', e.target.value)} data-testid="guardian-relationship" className={inputCls} />
                </Field>
              </div>
            )}
          </section>

          {/* Consents */}
          <section>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={16} className="text-[#c9a961]" strokeWidth={1.6} />
              <h2
                className="font-serif text-2xl text-[#1c1e20]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                Consents &amp; acknowledgments
              </h2>
            </div>
            <p className="text-[13px] text-[#6b6b6b] mb-6">Please review each item carefully. The first three are required to proceed.</p>

            <div className="space-y-5">
              <ConsentCheckbox
                checked={form.consent_treatment}
                onChange={(v) => set('consent_treatment', v)}
                label="Consent for Specimen Collection & Concierge Services (required)"
                description="I voluntarily consent to the specimen collection, phlebotomy, point-of-care testing, CPR instruction, and related concierge wellness services provided by Aurelia Concierge Wellness Partners and its licensed personnel. I understand the routine risks (mild bruising, discomfort, lightheadedness, rare infection) and have had the opportunity to ask questions."
                testId="consent-treatment"
              />
              <ConsentCheckbox
                checked={form.consent_hipaa}
                onChange={(v) => set('consent_hipaa', v)}
                label="HIPAA Notice of Privacy Practices (required)"
                description={
                  <>
                    I acknowledge that I have been provided access to Aurelia&rsquo;s{' '}
                    <Link
                      to="/hipaa-notice"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="hipaa-notice-link"
                      className="text-[#8a7132] underline hover:text-[#c9a961] font-medium"
                    >
                      Notice of Privacy Practices
                    </Link>. I understand my protected health information (PHI) may be used or
                    disclosed for treatment, payment, and healthcare operations, and only shared with
                    third parties as permitted or required by law.
                  </>
                }
                testId="consent-hipaa"
              />
              <ConsentCheckbox
                checked={form.consent_financial}
                onChange={(v) => set('consent_financial', v)}
                label="Financial Responsibility (required)"
                description="I understand that Aurelia collects payment before services are rendered through its secure Square point-of-sale system and also accepts Afterpay (buy-now-pay-later). Aurelia's concierge fees are separate from any laboratory or facility fees billed by third-party labs. I accept financial responsibility for services rendered and agree to Aurelia's posted cancellation policy."
                testId="consent-financial"
              />
              <ConsentCheckbox
                checked={form.consent_media}
                onChange={(v) => set('consent_media', v)}
                label="Photography / Media Release (optional)"
                description="I grant Aurelia permission to capture non-identifying photography for internal training or marketing purposes. I understand this is optional and does not affect my care."
                testId="consent-media"
              />
            </div>
          </section>

          {/* Signature */}
          <section>
            <div className="flex items-center gap-2 mb-1">
              <ClipboardCheck size={16} className="text-[#c9a961]" strokeWidth={1.6} />
              <h2
                className="font-serif text-2xl text-[#1c1e20]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                Electronic signature
              </h2>
            </div>
            <p className="text-[13px] text-[#6b6b6b] mb-5">
              Type your full legal name exactly as entered above. Your typed name serves as your legal
              signature under the Electronic Signatures in Global and National Commerce Act (E-SIGN).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="TYPED SIGNATURE" required testId="signature">
                <input
                  required
                  value={form.signature}
                  onChange={(e) => set('signature', e.target.value)}
                  data-testid="signature"
                  placeholder="Type your full legal name"
                  className={`${inputCls} italic`}
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px' }}
                />
              </Field>
              <Field label="DATE">
                <input readOnly value={new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} className={`${inputCls} bg-[#faf6f0] text-[#6b6b6b]`} />
              </Field>
            </div>
          </section>

          {error && (
            <p className="text-rose-600 text-sm" data-testid="consent-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="consent-submit"
            className="w-full md:w-auto bg-[#c9a961] hover:bg-[#b89751] transition-colors text-[#141618] tracking-[0.22em] text-[12px] font-semibold px-10 py-4 disabled:opacity-60"
          >
            {busy ? 'SUBMITTING\u2026' : 'SIGN & SUBMIT CONSENT'}
          </button>
          <p className="text-[11px] text-[#6b6b6b]">
            A copy of your signed form will be emailed to you and stored securely on Aurelia&rsquo;s protected records.
          </p>
        </form>
      </div>
    </div>
  );
};

export default PatientForms;
